import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import _ from 'lodash';
import {ButtonModule} from 'primeng/button';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {AutomationPolicyEditorComponent} from '@webmate-web-core/shared/components/browsersession/automation-policy-editor.component';
import {AutomationPolicyEditorMode} from '@webmate-web-core/shared/components/browsersession/automation-policy-editor.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {TOOL_CONTAINER_DATA} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component';
import {ModalToolContainerService, WebmateToolConfig} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';
import {ProjectId, TestObjectId} from '@webmate-web-core/shared/types/ids';
import {TestObject} from '@webmate-web-core/shared/types/testmgmt';
import {
    cloneAutomationPolicy,
    hasAutomationPolicy,
    isManualOnlyMatchingSpec,
    normalizeAutomationPolicy
} from '@webmate-web-core/shared/utils/testobject-automation-policy.util';
import {forkJoin, of} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';

interface PolicyConfigurationRow {
    testObject: TestObject;
    originalPolicy?: AutomationPolicyConfig;
    policyDraft?: AutomationPolicyConfig;
    expanded: boolean;
    saving: boolean;
    error?: string;
}

export class ConfigureTestObjectAutomationPoliciesToolConfig implements WebmateToolConfig {
    toolName = 'Configure Policies for New TestObjects';
    scrollable = true;

    constructor(
        public projectId: ProjectId,
        public newTestObjects: TestObject[],
        public existingTestObjects: TestObject[] = [],
        public onDone: () => void = () => undefined,
        public closeParentOnDone: boolean = true
    ) {
    }
}

@Component({
    selector: 'wm-configure-testobject-automation-policies-tool',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        ProgressSpinnerModule,
        AutomationPolicyEditorComponent,
    ],
    templateUrl: './configure-testobject-automation-policies-tool.component.html',
    styleUrls: ['./configure-testobject-automation-policies-tool.component.less']
})
export class ConfigureTestObjectAutomationPoliciesToolComponent implements OnInit {
    rows: PolicyConfigurationRow[] = [];
    bulkPolicy?: AutomationPolicyConfig;
    saving: boolean = false;

    constructor(
        @Inject(TOOL_CONTAINER_DATA) public config: ConfigureTestObjectAutomationPoliciesToolConfig,
        private readonly testService: TestService,
        private readonly notificationService: WmNotificationService,
        private readonly toolService: ModalToolContainerService
    ) {
    }

    ngOnInit(): void {
        this.rows = (this.config.newTestObjects || []).map((testObject, index) => ({
            testObject,
            originalPolicy: cloneAutomationPolicy(testObject.automationPolicy),
            policyDraft: cloneAutomationPolicy(testObject.automationPolicy),
            expanded: index === 0,
            saving: false,
        }));
    }

    get hasNewTestObjects(): boolean {
        return this.rows.length > 0;
    }

    get changedRows(): PolicyConfigurationRow[] {
        return this.rows.filter(row => this.isRowChanged(row));
    }

    get saveDisabled(): boolean {
        return this.saving || this.changedRows.length === 0;
    }

    get hasMixedPolicyTargets(): boolean {
        return this.rows.some(row => this.isMobileTestObject(row.testObject)) &&
            this.rows.some(row => !this.isMobileTestObject(row.testObject));
    }

    get bulkPolicyMode(): AutomationPolicyEditorMode {
        return this.rows.every(row => this.isMobileTestObject(row.testObject)) ? 'mobile' : 'web';
    }

    trackRow(_: number, row: PolicyConfigurationRow): string {
        return row.testObject.id;
    }

    trackTestObject(_: number, testObject: TestObject): string {
        return testObject.id;
    }

    toggleRow(row: PolicyConfigurationRow): void {
        row.expanded = !row.expanded;
    }

    clearRow(row: PolicyConfigurationRow): void {
        row.policyDraft = undefined;
    }

    applyBulkPolicy(): void {
        const policy = cloneAutomationPolicy(this.bulkPolicy);
        this.rows.forEach(row => {
            row.policyDraft = cloneAutomationPolicy(policy);
        });
    }

    clearAll(): void {
        this.rows.forEach(row => {
            row.policyDraft = undefined;
        });
    }

    hasPolicy(policy?: AutomationPolicyConfig | null): boolean {
        return hasAutomationPolicy(policy);
    }

    isManualOnly(row: PolicyConfigurationRow): boolean {
        return isManualOnlyMatchingSpec(row.testObject.matchingSpec);
    }

    isMobileTestObject(testObject: TestObject): boolean {
        return testObject.generationSpec?.type === 'MobileAppGenerationSpec';
    }

    isRowChanged(row: PolicyConfigurationRow): boolean {
        return !_.isEqual(
            normalizeAutomationPolicy(row.originalPolicy) || null,
            normalizeAutomationPolicy(row.policyDraft) || null
        );
    }

    getDisplayName(testObject: TestObject): string {
        return testObject.name?.trim() || this.getCanonicalSource(testObject) || testObject.id;
    }

    getDisplaySecondary(testObject: TestObject): string {
        return this.getCanonicalSource(testObject) || testObject.description || testObject.id;
    }

    save(): void {
        const rowsToSave = this.changedRows;
        if (rowsToSave.length === 0) {
            this.finishAndClose();
            return;
        }

        this.saving = true;
        rowsToSave.forEach(row => {
            row.saving = true;
            row.error = undefined;
        });

        forkJoin(rowsToSave.map(row => {
            const policy = normalizeAutomationPolicy(row.policyDraft) || null;
            return this.testService.setTestObjectAutomationPolicy$(row.testObject.id as TestObjectId, policy).pipe(
                take(1),
                map(() => ({row, policy, success: true as const})),
                catchError(err => {
                    console.error('Failed to save Test Object automation policy', err);
                    return of({row, policy, success: false as const});
                })
            );
        })).subscribe(results => {
            let failureCount = 0;
            results.forEach(result => {
                result.row.saving = false;

                if (!result.success) {
                    failureCount += 1;
                    result.row.error = 'Could not save this policy.';
                    return;
                }

                result.row.testObject.automationPolicy = result.policy;
                result.row.originalPolicy = cloneAutomationPolicy(result.policy);
                result.row.policyDraft = cloneAutomationPolicy(result.policy);
            });

            this.saving = false;

            if (failureCount > 0) {
                this.notificationService.errorNotification(`${failureCount} automation policy update${failureCount === 1 ? '' : 's'} could not be saved.`);
                return;
            }

            this.finishAndClose();
        });
    }

    skip(): void {
        this.finishAndClose();
    }

    private finishAndClose(): void {
        this.config.onDone?.();
        this.toolService.closeLastTool();
        if (this.config.closeParentOnDone) {
            this.toolService.closeLastTool();
        }
    }

    private getCanonicalSource(testObject: TestObject): string | undefined {
        const generationSpec = testObject.generationSpec as any;
        if (generationSpec?.type === 'URLGenerationSpec') {
            return generationSpec.url;
        }
        if (generationSpec?.type === 'ManualGenerationSpec') {
            return generationSpec.instructions;
        }
        if (generationSpec?.type === 'InteractionPathGenerationSpec') {
            const steps = generationSpec.interactionPath?.steps?.length;
            return steps ? `${steps} interaction step${steps === 1 ? '' : 's'}` : 'Interaction path';
        }
        if (generationSpec?.type === 'MobileAppGenerationSpec') {
            return generationSpec.packageId ? `Mobile package ${generationSpec.packageId}` : 'Mobile application';
        }
        return generationSpec?.type;
    }
}
